import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { tasksAPI, workingTasksAPI, groupsAPI } from '../services/api';
import '../styles/Tasks.css';

const Tasks = () => {
    const [tasks, setTasks] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [filterStatus, setFilterStatus] = useState('pending'); // 'all', 'pending', 'completed' - default to pending
    const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'duration', 'group'
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        group_id: '',
        expected_duration: '',
        expected_done_date: '',
        steps: []
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [tasksRes, groupsRes] = await Promise.all([
                tasksAPI.getAll(),
                groupsAPI.getAll()
            ]);
            setTasks(tasksRes.data.tasks);
            setGroups(groupsRes.data.groups);
            setLoading(false);
        } catch (error) {
            console.error('Error loading data:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddStep = () => {
        setFormData({
            ...formData,
            steps: [...formData.steps, { name: '', description: '', duration: '' }]
        });
    };

    const handleStepChange = (index, field, value) => {
        const newSteps = [...formData.steps];
        newSteps[index][field] = value;
        setFormData({ ...formData, steps: newSteps });
    };

    const handleRemoveStep = (index) => {
        const newSteps = formData.steps.filter((_, i) => i !== index);
        setFormData({ ...formData, steps: newSteps });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const taskData = {
                ...formData,
                expected_duration: parseInt(formData.expected_duration),
                group_id: formData.group_id ? parseInt(formData.group_id) : null,
                steps: formData.steps.map(s => ({
                    ...s,
                    duration: parseInt(s.duration)
                }))
            };

            if (editingTask) {
                await tasksAPI.update(editingTask.id, taskData);
            } else {
                await tasksAPI.create(taskData);
            }

            setShowModal(false);
            setEditingTask(null);
            setFormData({
                name: '',
                description: '',
                group_id: '',
                expected_duration: '',
                expected_done_date: '',
                steps: []
            });
            loadData();
        } catch (error) {
            console.error('Error saving task:', error);
            alert(error.response?.data?.error || 'Failed to save task');
        }
    };

    const handleEdit = (task) => {
        setEditingTask(task);
        
        // Format date to YYYY-MM-DD for date input
        let formattedDate = '';
        if (task.expected_done_date) {
            const date = new Date(task.expected_done_date);
            formattedDate = date.toISOString().split('T')[0];
        }
        
        setFormData({
            name: task.name,
            description: task.description || '',
            group_id: task.group_id || '',
            expected_duration: task.expected_duration,
            expected_done_date: formattedDate,
            steps: task.steps || []
        });
        setShowModal(true);
    };

    const handleDelete = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        
        try {
            await tasksAPI.delete(taskId);
            loadData();
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    };

    const handleAddToWorking = async (taskId) => {
        try {
            await workingTasksAPI.add(taskId);
            alert('Task added to working list!');
        } catch (error) {
            console.error('Error adding to working list:', error);
            alert(error.response?.data?.error || 'Failed to add task to working list');
        }
    };

    const openNewTaskModal = () => {
        setEditingTask(null);
        setFormData({
            name: '',
            description: '',
            group_id: '',
            expected_duration: '',
            expected_done_date: '',
            steps: []
        });
        setShowModal(true);
    };

    // Helper function to get date urgency color
    const getDateUrgencyColor = (expectedDate) => {
        if (!expectedDate) return null;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(expectedDate);
        dueDate.setHours(0, 0, 0, 0);
        
        const diffTime = dueDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) return '#ef4444'; // Red - overdue (nice red)
        if (diffDays <= 2) return '#f59e0b'; // Yellow/Orange - 2 days or less (nice amber)
        return '#10b981'; // Green - more than 2 days (nice emerald)
    };

    // Filter and sort tasks
    const filteredTasks = tasks.filter(task => {
        if (filterStatus === 'pending') return !task.is_completed;
        if (filterStatus === 'completed') return task.is_completed;
        return true; // 'all'
    });

    const sortedTasks = [...filteredTasks].sort((a, b) => {
        switch (sortBy) {
            case 'date':
                // Sort by expected_done_date, tasks without dates go to end
                if (!a.expected_done_date && !b.expected_done_date) return 0;
                if (!a.expected_done_date) return 1;
                if (!b.expected_done_date) return -1;
                return new Date(a.expected_done_date) - new Date(b.expected_done_date);
            case 'name':
                return a.name.localeCompare(b.name);
            case 'duration':
                return a.expected_duration - b.expected_duration;
            case 'group':
                const groupA = a.group_name || '';
                const groupB = b.group_name || '';
                return groupA.localeCompare(groupB);
            default:
                return 0;
        }
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <div className="main-content">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <div className="main-content">
                <div className="tasks-container">
                    <div className="tasks-header">
                        <div className="header-left">
                            <h1>📋 All Tasks</h1>
                            <div className="filter-group">
                                <label htmlFor="status-filter">Filter:</label>
                                <select 
                                    id="status-filter"
                                    className="status-filter"
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="all">All Tasks ({tasks.length})</option>
                                    <option value="pending">Pending ({tasks.filter(t => !t.is_completed).length})</option>
                                    <option value="completed">Completed ({tasks.filter(t => t.is_completed).length})</option>
                                </select>
                            </div>
                            <div className="filter-group">
                                <label htmlFor="sort-by">Sort by:</label>
                                <select 
                                    id="sort-by"
                                    className="status-filter"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    <option value="date">Due Date</option>
                                    <option value="name">Name</option>
                                    <option value="duration">Duration</option>
                                    <option value="group">Group</option>
                                </select>
                            </div>
                        </div>
                        <button onClick={openNewTaskModal} className="btn-primary">
                            + Add New Task
                        </button>
                    </div>

                    <div className="tasks-grid">
                        {sortedTasks.length === 0 ? (
                            <div className="empty-state">
                                <h2>No {filterStatus !== 'all' ? filterStatus : ''} tasks</h2>
                                <p>{filterStatus === 'all' ? 'Create your first task to get started!' : `You have no ${filterStatus} tasks.`}</p>
                            </div>
                        ) : (
                            sortedTasks.map((task) => (
                            <div 
                                key={task.id} 
                                className="task-card"
                                style={{ borderLeftColor: task.group_color || '#ccc' }}
                            >
                                <div className="task-card-header">
                                    {task.group_name && (
                                        <span 
                                            className="group-badge" 
                                            style={{ backgroundColor: task.group_color }}
                                        >
                                            {task.group_name}
                                        </span>
                                    )}
                                    {task.is_completed && <span className="completed-badge">✓ Completed</span>}
                                </div>

                                <h3>{task.name}</h3>
                                {task.description && <p className="task-description">{task.description}</p>}

                                <div className="task-meta">
                                    <div className="meta-item">
                                        <span className="icon">⏱️</span>
                                        <span>{task.expected_duration} min</span>
                                    </div>
                                    {task.expected_done_date && (
                                        <div className="meta-item">
                                            <span className="icon">📅</span>
                                            <span 
                                                className="date-badge"
                                                style={{ 
                                                    backgroundColor: getDateUrgencyColor(task.expected_done_date),
                                                    color: 'white',
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '4px',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                {new Date(task.expected_done_date).toLocaleDateString()}
                                            </span>
                                        </div>
                                    )}
                                    {task.total_steps > 0 && (
                                        <div className="meta-item">
                                            <span className="icon">📝</span>
                                            <span>{task.completed_steps}/{task.total_steps} steps</span>
                                        </div>
                                    )}
                                </div>

                                {task.steps && task.steps.length > 0 && (
                                    <div className="task-steps-preview">
                                        <h4>Steps:</h4>
                                        <ul>
                                            {task.steps.slice(0, 3).map((step, index) => (
                                                <li key={index} className={step.is_completed ? 'completed' : ''}>
                                                    {step.is_completed && '✓ '}
                                                    {step.name}
                                                </li>
                                            ))}
                                            {task.steps.length > 3 && <li>...and {task.steps.length - 3} more</li>}
                                        </ul>
                                    </div>
                                )}

                                <div className="task-actions">
                                    {!task.is_completed && (
                                        <button 
                                            onClick={() => handleAddToWorking(task.id)}
                                            className="btn-success"
                                        >
                                            ➕ Add to Working
                                        </button>
                                    )}
                                    <button onClick={() => handleEdit(task)} className="btn-secondary">
                                        ✏️ Edit
                                    </button>
                                    <button onClick={() => handleDelete(task.id)} className="btn-danger">
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                            ))
                        )}
                    </div>

                    {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{editingTask ? 'Edit Task' : 'New Task'}</h2>
                                <button onClick={() => setShowModal(false)} className="btn-close">×</button>
                            </div>

                            <form onSubmit={handleSubmit} className="task-form">
                                <div className="form-group">
                                    <label>Task Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter task name"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Enter task description"
                                        rows="3"
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Group</label>
                                        <select name="group_id" value={formData.group_id} onChange={handleInputChange}>
                                            <option value="">No Group</option>
                                            {groups.map(group => (
                                                <option key={group.id} value={group.id}>{group.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Expected Duration (minutes) *</label>
                                        <input
                                            type="number"
                                            name="expected_duration"
                                            value={formData.expected_duration}
                                            onChange={handleInputChange}
                                            required
                                            min="1"
                                            placeholder="30"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Due Date</label>
                                        <input
                                            type="date"
                                            name="expected_done_date"
                                            value={formData.expected_done_date}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                <div className="steps-section">
                                    <div className="steps-header">
                                        <h3>Steps</h3>
                                        <button type="button" onClick={handleAddStep} className="btn-secondary">
                                            + Add Step
                                        </button>
                                    </div>

                                    {formData.steps.map((step, index) => (
                                        <div key={index} className="step-form">
                                            <div className="step-form-header">
                                                <h4>Step {index + 1}</h4>
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleRemoveStep(index)}
                                                    className="btn-danger-small"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                value={step.name}
                                                onChange={(e) => handleStepChange(index, 'name', e.target.value)}
                                                placeholder="Step name"
                                                required
                                            />
                                            <textarea
                                                value={step.description}
                                                onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                                placeholder="Step description (optional)"
                                                rows="2"
                                            />
                                            <input
                                                type="number"
                                                value={step.duration}
                                                onChange={(e) => handleStepChange(index, 'duration', e.target.value)}
                                                placeholder="Duration (minutes)"
                                                min="1"
                                                required
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="modal-actions">
                                    <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        {editingTask ? 'Update Task' : 'Create Task'}
                                    </button>
                                </div>
                            </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Tasks;
